import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { EmployeeProvider } from './context/EmployeeContext';
import { MensajesProvider } from './context/MensajesContext';
import { DiaProvider } from './context/DiaContext';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';

// Code-splitting del arranque: el layout del dashboard (sidebar, navbar,
// búsqueda global, notificaciones, tour…) y el botón de WhatsApp traen
// framer-motion + decenas de íconos. Ninguno hace falta para pintar el
// landing, /login o /register, así que salen del bundle principal y se
// cargan solo cuando la ruta los necesita (Suspense ya envuelve las rutas).
const DashboardLayout    = lazy(() => import('./components/layout/DashboardLayout'));
const WhatsAppButton     = lazy(() => import('./components/WhatsAppButton'));

const Landing            = lazy(() => import('./pages/Landing'));
const Login              = lazy(() => import('./pages/Login'));
const Register           = lazy(() => import('./pages/Register'));
const Onboarding         = lazy(() => import('./pages/Onboarding'));
const Dashboard          = lazy(() => import('./pages/Dashboard'));
const Productos          = lazy(() => import('./pages/Productos'));
const POS                = lazy(() => import('./pages/POS'));
const Mesas              = lazy(() => import('./pages/Mesas'));
const Reportes           = lazy(() => import('./pages/Reportes'));
const Ventas             = lazy(() => import('./pages/Ventas'));
const MezoIA             = lazy(() => import('./pages/MezoIA'));
const Empleados          = lazy(() => import('./pages/Empleados'));
const MiTurno            = lazy(() => import('./pages/MiTurno'));
const Configuracion      = lazy(() => import('./pages/Configuracion'));
const Arqueo             = lazy(() => import('./pages/Arqueo'));
const PantallaCocina     = lazy(() => import('./pages/PantallaCocina'));
const Mensajes           = lazy(() => import('./pages/Mensajes'));
const Terminos           = lazy(() => import('./pages/Terminos'));
const Privacidad         = lazy(() => import('./pages/Privacidad'));
const SobreMezo          = lazy(() => import('./pages/SobreMezo'));
const CentroAyuda        = lazy(() => import('./pages/CentroAyuda'));
const EstadoSistema      = lazy(() => import('./pages/EstadoSistema'));
const Equipo             = lazy(() => import('./pages/Equipo'));
const TrabajaConNosotros = lazy(() => import('./pages/TrabajaConNosotros'));
const Blog               = lazy(() => import('./pages/Blog'));
const BlogArticulo       = lazy(() => import('./pages/BlogArticulo'));
const Novedades          = lazy(() => import('./pages/Novedades'));
const MenuPublico        = lazy(() => import('./pages/MenuPublico'));
const CuentaMesa         = lazy(() => import('./pages/CuentaMesa'));
const MenuQR             = lazy(() => import('./pages/MenuQR'));
const Promociones        = lazy(() => import('./pages/Promociones'));
const Inventario         = lazy(() => import('./pages/Inventario'));
const Planes             = lazy(() => import('./pages/Planes'));
const Clientes           = lazy(() => import('./pages/Clientes'));
const AdminNegocios      = lazy(() => import('./pages/AdminNegocios'));
const Propinas           = lazy(() => import('./pages/Propinas'));
const AdminLeads         = lazy(() => import('./pages/AdminLeads'));
const AdminCuenta        = lazy(() => import('./pages/AdminCuenta'));
const AdminCRM           = lazy(() => import('./pages/AdminCRM'));
const Guia               = lazy(() => import('./pages/Guia'));
const DemoEntrada        = lazy(() => import('./pages/DemoEntrada'));
const NotFound           = lazy(() => import('./pages/NotFound'));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#080706]">
      <div className="w-8 h-8 border-4 border-[#C8903F] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// Redirige / al dashboard si está logueado, o a la Landing si no
function RootRedirect() {
  const { user, negocio, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-mezo-ink">
        <div className="w-8 h-8 border-4 border-mezo-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (user) return <Navigate to={negocio ? '/dashboard' : '/onboarding'} replace />;
  return <Landing />;
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <ErrorBoundary>
      <AuthProvider>
        <ThemeProvider>
        <DiaProvider>
        <MensajesProvider>
        <EmployeeProvider>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Pública */}
            <Route path="/"          element={<RootRedirect />} />
            <Route path="/login"     element={<Login />} />
            <Route path="/register"  element={<Register />} />
            {/* Registro legacy compatible */}
            <Route path="/registro"  element={<Onboarding />} />
            <Route path="/onboarding" element={<Onboarding />} />
            {/* Páginas legales */}
            <Route path="/terminos"   element={<Terminos />} />
            <Route path="/privacidad" element={<Privacidad />} />
            <Route path="/sobre"      element={<SobreMezo />} />
            <Route path="/ayuda"      element={<CentroAyuda />} />
            <Route path="/estado"     element={<EstadoSistema />} />
            <Route path="/equipo"               element={<Equipo />} />
            <Route path="/trabaja-con-nosotros" element={<TrabajaConNosotros />} />
            <Route path="/blog"                 element={<Blog />} />
            <Route path="/blog/:slug"           element={<BlogArticulo />} />
            <Route path="/novedades"            element={<Novedades />} />

            {/* Menú público con QR — sin login. Se abre al escanear el QR en la mesa. */}
            <Route path="/menu/:businessId"     element={<MenuPublico />} />
            <Route path="/cuenta/:businessId/:mesa" element={<CuentaMesa />} />
            <Route path="/cuenta/:businessId"       element={<CuentaMesa />} />

            {/* Panel super admin de mezo (solo juaneslizcanom + temetcol).
                La RPC valida el email — un curioso que entre directo a
                /admin-mezo verá la página vacía con error 401. */}
            <Route path="/admin-mezo"            element={<AdminCRM />} />
            <Route path="/admin-mezo/negocios"   element={<AdminNegocios />} />
            <Route path="/admin-mezo/leads"      element={<AdminLeads />} />
            <Route path="/admin-mezo/cuenta/:id" element={<AdminCuenta />} />

            {/* Guía de bienvenida — pública, para mandar el link a clientes
                nuevos por email. Imprimible como PDF (Ctrl+P). */}
            <Route path="/guia"                 element={<Guia />} />

            {/* Demo pública — activa el modo mock con datos de ejemplo y
                recarga hacia /dashboard. Sin registro. */}
            <Route path="/demo"                 element={<DemoEntrada />} />

            {/* Protegidas — sidebar layout */}
            <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route path="/dashboard"     element={<Dashboard />} />
              <Route path="/inicio"        element={<Dashboard />} />
              <Route path="/pos"           element={<POS />} />
              <Route path="/mesas"         element={<Mesas />} />
              <Route path="/cocina"        element={<PantallaCocina />} />
              <Route path="/arqueo"        element={<Arqueo />} />
              <Route path="/inventario"    element={<Inventario />} />
              <Route path="/reportes"      element={<Reportes />} />
              <Route path="/propinas"      element={<Propinas />} />
              <Route path="/ventas"        element={<Ventas />} />
              <Route path="/mezo-ia"       element={<MezoIA />} />
              <Route path="/empleados"     element={<Empleados />} />
              {/* Asistencia con evidencia (foto + GPS): cada empleado marca su turno */}
              <Route path="/mi-turno"      element={<MiTurno />} />
              <Route path="/productos"     element={<Productos />} />
              <Route path="/mensajes"      element={<Mensajes />} />
              <Route path="/configuracion" element={<Configuracion />} />
              <Route path="/planes"        element={<Planes />} />
              <Route path="/clientes"      element={<Clientes />} />
              <Route path="/menu-qr"       element={<MenuQR />} />
              <Route path="/promociones"   element={<Promociones />} />
            </Route>

            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>

          {/* Botón flotante de WhatsApp (solo /ayuda, /planes, /sobre): lazy y
              con fallback vacío para no bloquear ni parpadear el arranque. */}
          <Suspense fallback={null}>
            <WhatsAppButton />
          </Suspense>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 3500,
              style: {
                // Vars del tema → el toast sigue al modo claro/oscuro (se
                // renderiza en un portal sobre <body>, data-theme está en <html>).
                background:   'var(--mezo-ink-raised)',
                color:        'var(--mezo-cream)',
                border:       '1px solid var(--mezo-ink-line)',
                borderRadius: 10,
                fontSize:     14,
                fontFamily:   '"DM Sans", system-ui, sans-serif',
                boxShadow:    '0 4px 20px rgba(0,0,0,0.18)',
              },
              success: { iconTheme: { primary: '#3DAA68', secondary: '#0D0B09' } },
              error:   { iconTheme: { primary: '#C8573F', secondary: '#0D0B09' } },
              loading: { iconTheme: { primary: '#C8903F', secondary: '#0D0B09' } },
            }}
          />
        </EmployeeProvider>
        </MensajesProvider>
        </DiaProvider>
        </ThemeProvider>
      </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
